import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileUpload, IconButton, IRestaurant, LocalinaLogo, SectionLabel } from '@localina/core';
import { CloseIcon } from '@localina/icons';

interface IProps {
    restaurant: IRestaurant;
    onChange: (image?: File) => void;
}

export const RestaurantLogoIntegration: React.FC<IProps> = (props) => {
    const { t } = useTranslation();

    const [image, setImage] = React.useState<File | undefined>(undefined);
    const [imageSrc, setImageSrc] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        const { logo } = props.restaurant.configuration;
        if (logo) {
            const substrings = logo.split('/');
            const name = substrings[substrings.length - 1];
            const file = new File([], name);
            setImage(file);
            setImageSrc(`${window.location.origin}/${logo}`);
            props.onChange(file);
        }
    }, []);

    const handleImageChange = (item?: File) => {
        setImage(item);
        setImageSrc(item ? URL.createObjectURL(item) : undefined);
        props.onChange(item);
    };

    const handleImageClear = () => {
        handleImageChange(undefined);
    };

    const isWhiteLabeled = Boolean(props.restaurant.configuration.widgetWhiteLabeled);

    return (
        <div className="integration logo">
            <SectionLabel value={t('integrations.items.logo.previewLabel')} />
            <div className="logo-preview">
                <div className="logo-preview__header">
                    {imageSrc ? <img className="logo" src={imageSrc} alt="" /> : !isWhiteLabeled && <LocalinaLogo />}
                </div>
            </div>
            {!imageSrc && (
                <p className="integration-hint">
                    {t(`integrations.items.logo.${isWhiteLabeled ? 'whiteLabelHint' : 'defaultLogoHint'}`)}
                </p>
            )}

            <SectionLabel value={t('integrations.items.logo.sections.file')} />
            <div className="integration-field-action">
                <div className={`logo-file${image ? '' : ' is-empty'}`}>
                    <span className="logo-file__name">{image?.name || t('integrations.items.logo.fields.image')}</span>
                    {image && (
                        <IconButton
                            className="logo-file__remove"
                            icon={<CloseIcon />}
                            ariaLabel={t('integrations.items.logo.remove')}
                            onClick={handleImageClear}
                        />
                    )}
                </div>
                <FileUpload
                    accept="image/*"
                    label={t('integrations.items.logo.buttons.upload')}
                    onChange={handleImageChange}
                />
            </div>
        </div>
    );
};
